import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AddClientModal from "../../components/domains/advisor-portal/add-new-client/AddClientModal";
import Breadcrumbs from "../../components/shared/Breadcrumbs";
import SubPageLayout from "../../components/shared/SubPageLayout";

import logger from '../../utils/logger';

// AI Chat Assistant Component
const AIChatAssistant = ({ onMessageSend }) => {
  const [message, setMessage] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {isMinimized ? (
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow-xl border hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <span className="text-gray-900 dark:text-gray-100">Open AI Assistant</span>
        </button>
      ) : (
        <div className="w-80 bg-surface-default dark:bg-surface-default rounded-lg shadow-xl border">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              AI Assistant
            </h3>
            <button
              onClick={() => setIsMinimized(true)}
              className="text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
            >
              ✕
            </button>
          </div>
          <div className="h-96 p-4 overflow-y-auto">
            {/* Chat messages will go here */}
            <div className="mb-4">
              <div className="bg-blue-100 rounded-lg p-3">
                <span className="text-gray-900 dark:text-gray-100 text-sm">
                  Can I help you with a digital workflow today?
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="flex-1 p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                placeholder="Type your message..."
              />
              <button
                onClick={() => {
                  onMessageSend(message);
                  setMessage("");
                }}
                className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stepper Component
const Stepper = ({ currentStep, steps }) => {
  return (
    <div className="py-4">
      <div className="flex items-center justify-center space-x-4">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  index < currentStep
                    ? "bg-[#029831] text-white"
                    : index === currentStep
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600 dark:text-gray-400"
                }`}
              >
                {index + 1}
              </div>
              <div className="ml-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                {step}
              </div>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-20 h-1 ${
                  index < currentStep ? "bg-[#029831]" : "bg-gray-200"
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Account Type Card Component
const AccountTypeCard = ({ type, description, selected, onClick }) => (
  <div
    onClick={onClick}
    className={`p-4 border rounded-lg cursor-pointer transition-all ${
      selected
        ? "border-[#029831] bg-green-50"
        : "border-gray-300 hover:border-gray-400"
    }`}
  >
    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{type}</h3>
    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
  </div>
);

// Additional Form Checkbox
const FormCheckbox = ({ label, checked, onChange }) => (
  <label className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 text-[#029831] border-gray-300 rounded focus:ring-[#029831]"
    />
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
  </label>
);

// Configuration Section Component
const ConfigurationSection = ({ title, children }) => (
  <div className="mb-8">
    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
    <div className="bg-gray-50 rounded-lg p-6">{children}</div>
  </div>
);

// Form Field Component
const FormField = ({
  label,
  type = "text",
  required,
  value,
  onChange,
  placeholder,
  options,
}) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {type === "select" ? (
      <select
        value={value}
        onChange={onChange}
        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
      >
        <option value="" className="text-gray-900 dark:text-gray-100">
          Select...
        </option>
        {options?.map((option) => (
          <option
            key={option.value}
            value={option.value}
            className="text-gray-900 dark:text-gray-100"
          >
            {option.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
      />
    )}
  </div>
);

// Document Upload Card Component
const DocumentUploadCard = ({
  title,
  description,
  required,
  onUpload,
  file,
}) => (
  <div className="border rounded-lg p-4">
    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-1">
      {title}
      {required && <span className="text-red-500 ml-1">*</span>}
    </h4>
    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{description}</p>
    {file ? (
      <div className="flex items-center justify-between bg-green-50 p-2 rounded">
        <span className="text-sm text-green-700">{file.name}</span>
        <button
          onClick={() => onUpload(null)}
          className="text-red-600 hover:text-red-800 text-sm"
        >
          Remove
        </button>
      </div>
    ) : (
      <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6">
        <label className="cursor-pointer">
          <input
            type="file"
            className="hidden"
            onChange={(e) => onUpload(e.target.files[0])}
          />
          <span className="px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200">
            Choose File
          </span>
        </label>
      </div>
    )}
  </div>
);

// Review Section Component
const ReviewSection = ({ title, data }) => (
  <div className="mb-6">
    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
    <div className="bg-gray-50 rounded-lg p-4">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(data).map(([key, value]) => (
          <div key={key}>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">{key}</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {typeof value === "boolean"
                ? value
                  ? "Yes"
                  : "No"
                : value || "Not provided"}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  </div>
);

const ClientOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedAccountTypes, setSelectedAccountTypes] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);
  const [accountConfig, setAccountConfig] = useState({
    tradingAuthorization: false,
    marginTrading: false,
    onlineAccess: true,
    beneficiaries: [],
    sourceOfFunds: "",
    investmentObjectives: "",
    riskTolerance: "",
    timeHorizon: "",
    employmentStatus: "",
    annualIncome: "",
    netWorth: "",
    taxBracket: "",
  });
  const [documents, setDocuments] = useState({
    identification: null,
    proofOfAddress: null,
    employmentVerification: null,
    additionalDocs: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const navigate = useNavigate();

  const steps = [
    "Client Selection",
    "Account Type",
    "Account Configuration",
    "Document Upload",
    "Review & Submit",
  ];

  const accountTypes = [
    {
      id: "individual",
      type: "Individual",
      description: "Standard individual brokerage account",
    },
    {
      id: "joint",
      type: "Joint Tenant",
      description: "Shared account with rights of survivorship",
    },
    {
      id: "ira",
      type: "IRA",
      description: "Individual Retirement Account",
    },
    {
      id: "trust",
      type: "Trust",
      description: "Trust account for estate planning",
    },
  ];

  const additionalForms = [
    {
      id: "moneylink",
      label: "MoneyLink (ACH Transfer)",
    },
    {
      id: "asset_transfer",
      label: "Asset Transfer Authorization",
    },
    {
      id: "beneficiary",
      label: "Beneficiary Designation",
    },
    {
      id: "trading_auth",
      label: "Trading Authorization",
    },
  ];

  // Mock client data - replace with actual API call
  const mockClients = [
    { id: 1, name: "John Doe", email: "john@example.com" },
    { id: 2, name: "Jane Smith", email: "jane@example.com" },
  ];

  const handleStartWorkflow = () => {
    setCurrentStep(1);
  };

  const handleClientSearch = (value) => {
    setSearchTerm(value);
    // Implement actual client search logic here
  };

  const handleAIMessage = (message) => {
    // Implement AI chat logic here
    logger.info("AI message:", message);
  };

  const handleAccountTypeSelect = (accountId) => {
    setSelectedAccountTypes((prev) => {
      if (prev.includes(accountId)) {
        return prev.filter((id) => id !== accountId);
      }
      return [...prev, accountId];
    });
  };

  const handleFormSelect = (formId) => {
    setSelectedForms((prev) => {
      if (prev.includes(formId)) {
        return prev.filter((id) => id !== formId);
      }
      return [...prev, formId];
    });
  };

  const handleConfigChange = (field, value) => {
    setAccountConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddBeneficiary = () => {
    setAccountConfig((prev) => ({
      ...prev,
      beneficiaries: [
        ...prev.beneficiaries,
        {
          id: Date.now(),
          firstName: "",
          lastName: "",
          relationship: "",
          percentage: "",
          dateOfBirth: "",
        },
      ],
    }));
  };

  const handleBeneficiaryChange = (id, field, value) => {
    setAccountConfig((prev) => ({
      ...prev,
      beneficiaries: prev.beneficiaries.map((ben) =>
        ben.id === id ? { ...ben, [field]: value } : ben,
      ),
    }));
  };

  const handleRemoveBeneficiary = (id) => {
    setAccountConfig((prev) => ({
      ...prev,
      beneficiaries: prev.beneficiaries.filter((ben) => ben.id !== id),
    }));
  };

  const handleDocumentUpload = (type, file) => {
    setDocuments((prev) => ({
      ...prev,
      [type]: file,
    }));
  };

  const handleAdditionalDocUpload = (file) => {
    setDocuments((prev) => ({
      ...prev,
      additionalDocs: [...prev.additionalDocs, file],
    }));
  };

  const handleRemoveAdditionalDoc = (index) => {
    setDocuments((prev) => ({
      ...prev,
      additionalDocs: prev.additionalDocs.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Implement your submission logic here
      logger.info("Submitting:", {
        client: selectedClient,
        accountTypes: selectedAccountTypes,
        forms: selectedForms,
        config: accountConfig,
        documents,
      });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Navigate to success page or show success message
      alert("Account opening workflow submitted successfully!");
      navigate("/advisor-portal/clients");
    } catch (error) {
      logger.error("Error submitting workflow:", error);
      alert("Error submitting workflow. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Advisor Portal", path: "/advisor-portal" },
        {
          label: "Client Onboarding",
          path: "/advisor-portal/client-onboarding",
        },
      ]}
      title="Client Onboarding"
      subtitle="Quickly open and configure client accounts with step-by-step guidance"
    >
      {/* AI Chat Assistant */}
      <AIChatAssistant onMessageSend={handleAIMessage} />
      {currentStep === 0 ? (
        // Landing Page
        <div className="text-center py-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Simplify Your Onboarding Process
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            Quickly open and configure client accounts with step-by-step
            guidance.
          </p>
          <button
            onClick={handleStartWorkflow}
            className="px-8 py-3 bg-[#029831] text-white rounded-lg hover:bg-[#018529] text-lg font-medium"
          >
            Start Digital Workflow
          </button>
        </div>
      ) : (
        // Workflow Steps
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6">
          <Stepper currentStep={currentStep - 1} steps={steps} />
          {currentStep === 1 && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Select or Add Client
              </h2>
              <div className="max-w-2xl mx-auto">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Search Existing Clients
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => handleClientSearch(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
                      placeholder="Search by name, account number, or SSN..."
                    />
                    {searchTerm && (
                      <div className="absolute w-full mt-1 bg-surface-default dark:bg-surface-default border border-gray-300 rounded-lg shadow-lg dark:shadow-none z-10">
                        {mockClients.map((client) => (
                          <div
                            key={client.id}
                            className="p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer text-gray-900 dark:text-gray-100"
                            onClick={() => setSelectedClient(client)}
                          >
                            <div className="font-medium">{client.name}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {client.email}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <div className="px-4 text-gray-500 dark:text-gray-500">or</div>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>
                <div className="mt-6">
                  <button
                    onClick={() => setShowAddClientModal(true)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Add New Client
                  </button>
                </div>
              </div>
            </div>
          )}
          {currentStep === 2 && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Select Account Type(s)
              </h2>
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {accountTypes.map((account) => (
                    <AccountTypeCard
                      key={account.id}
                      type={account.type}
                      description={account.description}
                      selected={selectedAccountTypes.includes(account.id)}
                      onClick={() => handleAccountTypeSelect(account.id)}
                    />
                  ))}
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Additional Forms and Authorizations
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {additionalForms.map((form) => (
                        <FormCheckbox
                          key={form.id}
                          label={form.label}
                          checked={selectedForms.includes(form.id)}
                          onChange={() => handleFormSelect(form.id)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentStep === 3 && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Account Configuration
              </h2>
              <div className="max-w-4xl mx-auto">
                <ConfigurationSection title="Account Features">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormCheckbox
                      label="Enable Trading Authorization"
                      checked={accountConfig.tradingAuthorization}
                      onChange={() =>
                        handleConfigChange(
                          "tradingAuthorization",
                          !accountConfig.tradingAuthorization,
                        )
                      }
                    />
                    <FormCheckbox
                      label="Enable Margin Trading"
                      checked={accountConfig.marginTrading}
                      onChange={() =>
                        handleConfigChange(
                          "marginTrading",
                          !accountConfig.marginTrading,
                        )
                      }
                    />
                  </div>
                </ConfigurationSection>

                <ConfigurationSection title="Investment Profile">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Source of Funds"
                      type="select"
                      required
                      value={accountConfig.sourceOfFunds}
                      onChange={(e) =>
                        handleConfigChange("sourceOfFunds", e.target.value)
                      }
                      options={[
                        { value: "employment", label: "Employment Income" },
                        { value: "investments", label: "Investment Income" },
                        { value: "inheritance", label: "Inheritance" },
                        { value: "business", label: "Business Income" },
                      ]}
                    />
                    <FormField
                      label="Investment Objectives"
                      type="select"
                      required
                      value={accountConfig.investmentObjectives}
                      onChange={(e) =>
                        handleConfigChange(
                          "investmentObjectives",
                          e.target.value,
                        )
                      }
                      options={[
                        { value: "growth", label: "Growth" },
                        { value: "income", label: "Income" },
                        {
                          value: "preservation",
                          label: "Capital Preservation",
                        },
                        { value: "speculation", label: "Speculation" },
                      ]}
                    />
                    <FormField
                      label="Risk Tolerance"
                      type="select"
                      required
                      value={accountConfig.riskTolerance}
                      onChange={(e) =>
                        handleConfigChange("riskTolerance", e.target.value)
                      }
                      options={[
                        { value: "conservative", label: "Conservative" },
                        { value: "moderate", label: "Moderate" },
                        { value: "aggressive", label: "Aggressive" },
                      ]}
                    />
                    <FormField
                      label="Time Horizon"
                      type="select"
                      required
                      value={accountConfig.timeHorizon}
                      onChange={(e) =>
                        handleConfigChange("timeHorizon", e.target.value)
                      }
                      options={[
                        { value: "short", label: "Short Term (< 3 years)" },
                        { value: "medium", label: "Medium Term (3-10 years)" },
                        { value: "long", label: "Long Term (> 10 years)" },
                      ]}
                    />
                  </div>
                </ConfigurationSection>

                {selectedForms.includes("beneficiary") && (
                  <ConfigurationSection title="Beneficiary Information">
                    {accountConfig.beneficiaries.map((beneficiary, index) => (
                      <div
                        key={beneficiary.id}
                        className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700 last:border-0"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">
                            Beneficiary {index + 1}
                          </h4>
                          <button
                            onClick={() =>
                              handleRemoveBeneficiary(beneficiary.id)
                            }
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            label="First Name"
                            required
                            value={beneficiary.firstName}
                            onChange={(e) =>
                              handleBeneficiaryChange(
                                beneficiary.id,
                                "firstName",
                                e.target.value,
                              )
                            }
                          />
                          <FormField
                            label="Last Name"
                            required
                            value={beneficiary.lastName}
                            onChange={(e) =>
                              handleBeneficiaryChange(
                                beneficiary.id,
                                "lastName",
                                e.target.value,
                              )
                            }
                          />
                          <FormField
                            label="Relationship"
                            type="select"
                            required
                            value={beneficiary.relationship}
                            onChange={(e) =>
                              handleBeneficiaryChange(
                                beneficiary.id,
                                "relationship",
                                e.target.value,
                              )
                            }
                            options={[
                              { value: "spouse", label: "Spouse" },
                              { value: "child", label: "Child" },
                              { value: "parent", label: "Parent" },
                              { value: "sibling", label: "Sibling" },
                              { value: "other", label: "Other" },
                            ]}
                          />
                          <FormField
                            label="Percentage"
                            type="number"
                            required
                            value={beneficiary.percentage}
                            onChange={(e) =>
                              handleBeneficiaryChange(
                                beneficiary.id,
                                "percentage",
                                e.target.value,
                              )
                            }
                            placeholder="Enter percentage (0-100)"
                          />
                          <FormField
                            label="Date of Birth"
                            type="date"
                            required
                            value={beneficiary.dateOfBirth}
                            onChange={(e) =>
                              handleBeneficiaryChange(
                                beneficiary.id,
                                "dateOfBirth",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={handleAddBeneficiary}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add Beneficiary
                    </button>
                  </ConfigurationSection>
                )}
              </div>
            </div>
          )}
          {currentStep === 4 && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Document Upload
              </h2>
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DocumentUploadCard
                    title="Government ID"
                    description="Upload a valid government-issued photo ID"
                    required
                    file={documents.identification}
                    onUpload={(file) =>
                      handleDocumentUpload("identification", file)
                    }
                  />
                  <DocumentUploadCard
                    title="Proof of Address"
                    description="Recent utility bill or bank statement (last 3 months)"
                    required
                    file={documents.proofOfAddress}
                    onUpload={(file) =>
                      handleDocumentUpload("proofOfAddress", file)
                    }
                  />
                  {selectedAccountTypes.includes("ira") && (
                    <DocumentUploadCard
                      title="Employment Verification"
                      description="Recent pay stub or employment letter"
                      required
                      file={documents.employmentVerification}
                      onUpload={(file) =>
                        handleDocumentUpload("employmentVerification", file)
                      }
                    />
                  )}
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Additional Documents
                  </h3>
                  <div className="space-y-4">
                    {documents.additionalDocs.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                      >
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {doc.name}
                        </span>
                        <button
                          onClick={() => handleRemoveAdditionalDoc(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) =>
                            handleAdditionalDocUpload(e.target.files[0])
                          }
                        />
                        <span className="px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200">
                          Add Additional Document
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentStep === 5 && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Review & Submit
              </h2>
              <div className="max-w-4xl mx-auto">
                <ReviewSection
                  title="Client Information"
                  data={{
                    "Full Name": `${selectedClient?.firstName} ${selectedClient?.lastName}`,
                    Email: selectedClient?.email,
                    Phone: selectedClient?.phone,
                  }}
                />

                <ReviewSection
                  title="Account Types"
                  data={Object.fromEntries(
                    selectedAccountTypes.map((type) => [
                      accountTypes.find((a) => a.id === type)?.type,
                      "Selected",
                    ]),
                  )}
                />

                <ReviewSection
                  title="Account Configuration"
                  data={{
                    "Trading Authorization": accountConfig.tradingAuthorization,
                    "Margin Trading": accountConfig.marginTrading,
                    "Source of Funds": accountConfig.sourceOfFunds,
                    "Investment Objectives": accountConfig.investmentObjectives,
                    "Risk Tolerance": accountConfig.riskTolerance,
                    "Time Horizon": accountConfig.timeHorizon,
                  }}
                />

                <ReviewSection
                  title="Documents"
                  data={{
                    "Government ID":
                      documents.identification?.name || "Not uploaded",
                    "Proof of Address":
                      documents.proofOfAddress?.name || "Not uploaded",
                    "Employment Verification":
                      documents.employmentVerification?.name || "Not required",
                    "Additional Documents":
                      documents.additionalDocs.length > 0
                        ? `${documents.additionalDocs.length} document(s) uploaded`
                        : "None",
                  }}
                />

                <div className="mt-8">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-[#029831] border-gray-300 rounded focus:ring-[#029831]"
                      required
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      I confirm that all information provided is accurate and
                      complete
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep((prev) => prev - 1)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                disabled={isSubmitting}
              >
                Back
              </button>
            )}
            {currentStep < steps.length ? (
              <button
                onClick={() => setCurrentStep((prev) => prev + 1)}
                className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]"
                disabled={
                  isSubmitting ||
                  (currentStep === 2 && selectedAccountTypes.length === 0) ||
                  (currentStep === 3 && !accountConfig.sourceOfFunds) ||
                  (currentStep === 4 &&
                    (!documents.identification || !documents.proofOfAddress))
                }
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit for Review"}
              </button>
            )}
          </div>
        </div>
      )}
      {/* Add Client Modal */}
      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
      />
    </SubPageLayout>
  );
};

export default ClientOnboarding;
